import { formatPrice } from '@getjust/commons';
import { Cart } from '@getjust/leaf';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { StackProps } from '@chakra-ui/react';

import Coupon from '$components/SvgLogos/Coupon';
import { useAddDiscount, useDeleteDiscount } from '$hooks/mutations';
import { useCart, useShop } from '$hooks/queries';
import { LoyaltyRewards } from '$src/components/LoyaltyRewards';
import { useIsDesktop } from '$src/hooks/client/useIsDesktop';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useTrackTransition } from '$src/hooks/client/useTrackTransition';
import { useClearGiftCards } from '$src/hooks/mutations/useClearGiftCards';
import { useUpdateLineItems } from '$src/hooks/mutations/useUpdateLineItems';
import { useCartLineItemsMutatingState } from '$src/hooks/state/useCartLineItemsMutatingState';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { useCorrectionsAtom } from '$src/hooks/state/useCorrectionsAtom';
import { useRecommendedDiscountsAtom } from '$src/hooks/state/useRecommendedDiscountsAtom';

interface CartDetailProps extends StackProps {
  showTotal?: boolean;
  showEmailOnly?: boolean;
}

const CartDetail = ({ showTotal = true, showEmailOnly = false, ...props }: CartDetailProps) => {
  const { mutateAsync: updateLineItems } = useUpdateLineItems();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const { error: discountError, addDiscount, status } = useAddDiscount();
  const { mutateAsync: deleteDiscount } = useDeleteDiscount();
  const { mutateAsync: clearGiftCards } = useClearGiftCards();
  const { t } = useTranslation('common');
  const { data: cart } = useCart();
  const { track } = useTrackAnalytics();
  const { corrections, initCorrections, clearCorrections } = useCorrectionsAtom();
  const { isMobile } = useIsDesktop();
  const [discountValue, setDiscountValue] = useState('');
  const { data: shop } = useShop();
  const { recommendedDiscountsAtom: recommendedDiscount } = useRecommendedDiscountsAtom();
  const { isItemMutating, pendingAdditions, isCartMutating } = useCartLineItemsMutatingState();

  const onDiscountAddClick = useCallback(
    async (code: string) => {
      track(AnalyticsEventName.DISCOUNT_CODE_SUBMITTED, { code });
      await addDiscount({
        code: code.trim(),
      });
    },
    [addDiscount, track],
  );

  const onDeleteDiscount = useCallback(
    (code: string) => {
      track(AnalyticsEventName.DISCOUNT_CODE_DELETE_SUBMITTED, { code });
      return deleteDiscount({ code });
    },
    [deleteDiscount, track],
  );

  const getLeftQuantityTitle = useCallback(
    (quantityAvailable: number | undefined, isAvailable: boolean): string => {
      if (!quantityAvailable && !isAvailable) return t('errors.out_of_stock');
      if (!quantityAvailable && isAvailable) return '';
      if (quantityAvailable === 1) return t('commons.leftQuantity', { count: quantityAvailable });
      return t('commons.leftQuantity_plurials', { count: quantityAvailable });
    },
    [t],
  );

  const onGiftCardRemove = useCallback(() => clearGiftCards(), [clearGiftCards]);

  const onUpdateItem = useCallback(
    async ({
      newQuantity,
      id,
      productId,
      variantId,
      customizationId,
    }: {
      id: string;
      productId: string | undefined;
      variantId: string | undefined;
      newQuantity: number;
      customizationId: string | undefined;
    }): Promise<number> => {
      const res = await updateLineItems({
        lineItems: [
          {
            id,
            productId,
            variantId,
            quantity: newQuantity,
            customizationId,
          },
        ],
      });

      if (!res.data.corrections?.length) {
        clearCorrections();
      } else {
        initCorrections(res.data.corrections);
      }

      // Get the actual applied quantity from the response
      const updatedLineItem = res.data.lineItems?.find((item) => item.id === id);
      return updatedLineItem?.quantity ?? newQuantity;
    },
    [updateLineItems, clearCorrections, initCorrections],
  );

  useEffect(() => {
    if (cart?.corrections?.length) {
      initCorrections(cart?.corrections);
    }
    return () => {
      clearCorrections();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shippingMessage = useMemo(() => {
    if (
      cart?.shipping?.selected &&
      cart?.shipping?.address &&
      cart?.shipping?.address?.firstName !== 'Just' && // JUST Users in Prestashop
      cart?.shipping?.address?.lastName !== 'Anonymous' && // mandatory to have estimated
      cart?.shipping?.address?.address1 !== '142 AVENUE LEDRU ROLLIN' // shipping in prestashop
    ) {
      return cart?.totalShipping === 0
        ? t('notAuthed.freeShipping')
        : `${formatPrice(cart?.totalShipping, {
            currency: cart?.currency ? cart.currency : 'EUR',
          })}`;
    }

    if (showEmailOnly) {
      return t('commons.next_step_calculating');
    } else {
      return t('commons.enter_address_to_calculate');
    }
  }, [cart, t, showEmailOnly]);

  const totalMessage = useMemo(() => {
    return {
      label: t('commons.total'),
      value: formatPrice(cart?.totalAmount ?? 0, { currency: cart?.currency ?? 'EUR' }),
    };
  }, [cart, t]);

  const recommendedDiscountCodes = useMemo(() => {
    return recommendedDiscount?.map((el) => el.code) ?? [];
  }, [recommendedDiscount]);

  useTrackTransition({
    value: recommendedDiscountCodes?.length > 0 && recommendedDiscountCodes?.length,
    trueEventName: AnalyticsEventName.FINDER_TRIGGERED,
    payload: {
      'Nb codes': recommendedDiscount?.length,
      codes: recommendedDiscount?.map((el) => el.code),
    },
  });

  const isDiscountHidden = canUseCheckoutFeature('hide_discount_input');

  return (
    <Cart
      discountCodeQuestion={t('commons.discount_code_question')}
      addDiscountLabel={t('buttons.add')}
      discountCodeDrawerTitle={t('commons.discount_code_title')}
      showEmailOnly={showEmailOnly}
      isDiscountHidden={isDiscountHidden}
      onDiscountAddClick={onDiscountAddClick}
      onDiscountRemove={onDeleteDiscount}
      onGiftCardRemove={onGiftCardRemove}
      discountApplyLabel={t('buttons.add_discount')}
      discountInputLabel={t('commons.discount_code')}
      discountFinderLabel={t('commons.discount_finder')}
      recommendedDiscountCodes={recommendedDiscountCodes}
      discountErrorLabel={
        status === 'error'
          ? (discountError.message ??
            (t('errors.commons.unknown_discount', { code: discountValue }) as string))
          : undefined
      }
      total={showTotal ? totalMessage : { label: '', value: '' }}
      subTotal={{
        label: !cart?.taxIncluded ? t('commons.subtotalHT') : t('commons.subtotal'),
        value: formatPrice((cart?.subTotal ?? 0) + (cart?.totalSale ?? 0), {
          currency: cart?.currency ?? 'EUR',
        }),
      }}
      shipping={{
        label: t('commons.shipping_method'),
        value: shippingMessage,
      }}
      tax={
        cart?.collectTax && !cart?.taxIncluded
          ? {
              value: cart?.taxReady
                ? formatPrice(cart?.totalTax ?? 0, {
                    currency: cart?.currency ?? 'EUR',
                  })
                : t('commons.calculating'),
              label: t('commons.tax'),
            }
          : undefined
      }
      products={(cart?.lineItems ?? []).map((lineItem) => ({
        ...lineItem,
        price: formatPrice(lineItem?.totalAmount, { currency: cart?.currency ?? 'EUR' }),
        variant: lineItem?.variants,
        image: lineItem?.imageUrl ?? '',
      }))}
      totalSale={
        cart?.totalSale
          ? {
              label: t('commons.sale'),
              value: formatPrice(cart?.totalSale ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      totalDiscount={
        cart?.totalDiscount
          ? {
              label: t('commons.discount'),
              value: formatPrice(cart?.totalDiscount ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      totalDuties={
        cart?.totalDuties
          ? {
              label: t('commons.duties'),
              value: formatPrice(cart?.totalDuties ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      discounts={cart?.discounts ?? []}
      giftCards={cart?.giftCards ?? []}
      totalGiftCard={
        cart?.totalGiftCard
          ? {
              label: t('commons.gift_card'),
              value: formatPrice(cart?.totalGiftCard ?? 0, {
                currency: cart?.currency ?? 'EUR',
              }),
            }
          : undefined
      }
      isQuantityDisabled={!shop?.features?.includes('quantity_selector') || showEmailOnly}
      isStockDisabled={!shop?.features?.includes('product_stock_indicator')}
      currency={cart?.currency ?? 'EUR'}
      shouldFocusDiscountField={false}
      updateQuantity={onUpdateItem}
      taxIncludedTitle={cart?.collectTax ? t('commons.taxIncludedTitle') : undefined}
      leftQuantityTitles={
        cart?.lineItems?.map((lineItem) =>
          getLeftQuantityTitle(lineItem.quantityAvailable, lineItem.isAvailable),
        ) ?? ['']
      }
      corrections={corrections ?? []}
      Icon={Coupon}
      availabilityMessage={
        corrections?.some((correction) => correction.reason === 'notFulfillable')
          ? t('errors.not_fulfillable_warning')
          : corrections?.length
            ? t('errors.availability_warning')
            : ''
      }
      updatedCartInformation={corrections?.length ? t('info.cart_updated') : ''}
      outOfStockMessage={corrections?.length ? t('errors.out_of_stock') : ''}
      notFulfillableMessage={corrections?.length ? t('errors.not_fulfillable') : ''}
      isMobile={isMobile}
      setDiscountValue={setDiscountValue}
      renderBeforeDiscounts={<LoyaltyRewards />}
      isItemLoading={isItemMutating}
      isAnyItemLoading={!!isCartMutating}
      pendingProducts={pendingAdditions}
      {...props}
    />
  );
};

export default CartDetail;
